import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { PaymentsOutlined as PaymentsIcon } from '@mui/icons-material';
import { keyframes } from '@mui/material/styles';
import { useReportBottomBannerHeight } from '../hooks/useReportBottomBannerHeight';

const borderBlink = keyframes`
  0%, 100% {
    border-color: #ed8936;
    box-shadow:
      0 0 0 1px rgba(237, 137, 54, 0.25),
      0 0 10px rgba(237, 137, 54, 0.35);
  }
  50% {
    border-color: #fbbf24;
    box-shadow:
      0 0 0 2px rgba(251, 191, 36, 0.75),
      0 0 24px rgba(251, 146, 60, 0.95),
      inset 0 0 14px rgba(251, 191, 36, 0.18);
  }
`;

interface PaymentBannerProps {
  amountSgd: number;
  onPay: () => void;
}

/** Fixed bottom banner prompting a user to pay the one-time setup fee. */
export default function PaymentBanner({ amountSgd, onPay }: PaymentBannerProps) {
  const ref = useReportBottomBannerHeight(12);

  return (
    <Box
      ref={ref}
      sx={{
        position: 'fixed', left: 4, right: 4, bottom: 4, zIndex: (t) => t.zIndex.appBar + 1,
        bgcolor: '#0f172a',
        color: '#ffffff',
        border: '3px solid #ed8936',
        borderRadius: 1.5,
        boxSizing: 'border-box',
        px: { xs: 2, md: 4 }, py: 1.5,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap',
        animation: `${borderBlink} 1s step-end infinite`,
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
        <PaymentsIcon sx={{ color: '#fb923c', flexShrink: 0, mt: 0.25 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: '#ffffff !important', fontWeight: 700, lineHeight: 1.5 }}>
            Your Super Connector setup is complete — the one-time setup fee of S${amountSgd} is now due.
          </Typography>
          <Typography variant="caption" sx={{ color: '#fdba74 !important', fontWeight: 600, display: 'block', mt: 0.25 }}>
            This fee will be waived for Super Accelerators
          </Typography>
        </Box>
      </Box>
      <Button
        variant="contained"
        size="small"
        onClick={onPay}
        sx={{
          bgcolor: '#ed8936',
          color: '#111827 !important',
          '&:hover': { bgcolor: '#f59e52' },
          textTransform: 'none',
          fontWeight: 800,
          flexShrink: 0,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}
      >
        Pay S${amountSgd} now
      </Button>
    </Box>
  );
}
